import {
    BUSINESS_DETAILS_FIELD_KEYS,
    BUSINESS_REFERENCE_FIELD_KEYS,
    BUSINESS_REFERENCES_COUNT,
    BUSINESS_REFERENCES_KEY,
    PINCODE_DIGIT_LENGTH,
} from '../../MandateFormConstants';
import { isValidPincode, validateTenDigitPhone } from '../../../../utils/utils';

// Whether the `businessDetails.*` slice of formData carries any actual value - an empty
// Business Details section (every field untouched) must not itself force a save. Shared by
// useManageDealDrawer's handleSaveMandateApplicants (save-fires-when-either gate) and the
// save-payload assembly (whether to include `businessDetails` at all).
export const hasBusinessDetailsData = (businessDetails: Record<string, any>): boolean =>
    Object.values(businessDetails || {}).some((value) => value !== '' && value !== null && value !== undefined);

// Business Address Pincode is optional but, when present, must be a well-formed 6-digit pincode -
// shared by BusinessDetailsSection's inline error and isBusinessDetailsValid below so both agree
// on the same rule.
export const isBusinessPincodeValid = (value: any): boolean => isValidPincode(value, PINCODE_DIGIT_LENGTH);

export type BusinessReference = { name: string; phone: string };

const BLANK_REFERENCE: BusinessReference = { name: '', phone: '' };
const REFERENCE_FIELD_KEY_LIST = Object.values(BUSINESS_REFERENCE_FIELD_KEYS);

// Optional, but when present exactly 10 digits (polus rejects non-numeric phones).
export const isReferencePhoneValid = (value: any): boolean => !value || validateTenDigitPhone(value);

// Pads/truncates a saved or in-progress references array to exactly BUSINESS_REFERENCES_COUNT rows,
// picking only {name, phone} so a legacy stored `type` key never rides along past this boundary.
export const normalizeBusinessReferences = (value: any): BusinessReference[] => {
    const list = Array.isArray(value) ? value : [];
    return Array.from({ length: BUSINESS_REFERENCES_COUNT }, (_, i) => ({
        name: list[i]?.name ?? BLANK_REFERENCE.name,
        phone: list[i]?.phone ?? BLANK_REFERENCE.phone,
    }));
};

// Whether any reference row carries a value - an untouched block must not itself force a save.
export const hasBusinessReferencesData = (refs: BusinessReference[]): boolean =>
    refs.some((ref) => REFERENCE_FIELD_KEY_LIST.some((key) => !!ref[key as keyof BusinessReference]?.trim()));

// A blank/partial reference row never blocks save - only a present-but-malformed phone is invalid.
export const areBusinessReferencesValid = (value: any): boolean =>
    normalizeBusinessReferences(value).every((ref) => isReferencePhoneValid(ref.phone));

// Whether the whole `businessDetails.*` slice is valid to save - mirrors isApplicantEntryValid's
// shape (a single composed predicate over the section's fields) so useManageDealDrawer's
// validateForm gates on both sections the same way.
export const isBusinessDetailsValid = (businessDetails: Record<string, any>): boolean =>
    isBusinessPincodeValid(businessDetails?.[BUSINESS_DETAILS_FIELD_KEYS.BUSINESS_ADDRESS_PINCODE]) &&
    areBusinessReferencesValid(businessDetails?.[BUSINESS_REFERENCES_KEY]);
